/**
 * ==============================================================================
 * WYSE BOOST SWATCH FETCHER (- Alex)
 * ==============================================================================
 * * PURPOSE:
 * asynchronously fetches and injects background-image/colour styles for product swatches
 * on collection/search pages. It solves the issue where swatches are missing when
 * products are loaded dynamically via AJAX (Boost).
 * * KEY FEATURES:
 * 1. Optimised Fetching: Requests `/products/handle?view=swatch-data` to load only
 * a tiny HTML snippet (1KB) instead of the full product page (100KB+).
 * 2. Session Caching: Stores fetched styles in sessionStorage to prevent redundant
 * network requests during a user's browsing session.
 * 3. Debouncing: Prevents API spamming while the user is actively filtering or scrolling.
 * 4. Error Handling: Gracefully hides swatches if the product handle returns a 404.
 * 5. Batching: Uses requestAnimationFrame to batch DOM updates for smooth rendering.
 */

(function () {
  // Singleton check: Prevent multiple instances of this script running
  if (window.wyseBoostSwatchLoaded) return;
  window.wyseBoostSwatchLoaded = true;

  // --- CONFIGURATION ---
  const CACHE_KEY = 'wyse_swatch_v1_';
  const DEBOUNCE_DELAY = 200; // Wait 200ms after DOM changes before fetching
  let debounceTimer;

  /**
   * Trigger the fetcher with a debounce delay.
   * Resets the timer if called rapidly (e.g., during fast scrolling).
   */
  const runFetcher = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchBoostSwatches, DEBOUNCE_DELAY);
  };

  /**
   * Main Logic:
   * 1. Scans DOM for empty swatches.
   * 2. Checks cache or fetches data.
   * 3. Updates the DOM.
   */
  const fetchBoostSwatches = () => {
    // Identify swatches that haven't been processed yet
    const swatches = document.querySelectorAll('.boost-js-fetch-swatch:not(.loaded)');
    if (swatches.length === 0) return;

    // Deduplicate handles to avoid fetching the same product multiple times per batch
    const uniqueHandles = new Set();
    swatches.forEach(s => uniqueHandles.add(s.getAttribute('data-swatch-handle')));

    // --- DOM UPDATER HELPER ---
    // Applies the background style to all matching swatch elements
    const applySwatchData = (handle, bgStyle) => {
      if (!bgStyle) return;

      // Batch DOM writes for performance
      requestAnimationFrame(() => {
        const waitingSwatches = document.querySelectorAll(`.boost-js-fetch-swatch[data-swatch-handle="${handle}"]`);
        waitingSwatches.forEach(ws => {
          ws.setAttribute('style', bgStyle);
          ws.classList.add('loaded');
          ws.style.opacity = '1'; // CSS transition fade-in
        });
      });
    };

    // Hides swatches that resulted in 404s or errors
    const hideBrokenSwatches = handle => {
      requestAnimationFrame(() => {
        const brokenSwatches = document.querySelectorAll(`.boost-js-fetch-swatch[data-swatch-handle="${handle}"]`);
        brokenSwatches.forEach(ws => {
          // Hide the list item container to prevent empty gaps
          const parent = ws.closest('li') || ws.parentElement;
          if (parent) parent.style.display = 'none';
        });
      });
    };

    // --- HTML PARSER ---
    // Extracts the specific style string from the fetched HTML snippet
    const parseAndApply = (handle, html) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Selector matches the specific element returned by 'view=swatch-data'
      const activeSwatch = doc.querySelector('.product-swatches__swatch-inner');

      if (activeSwatch) {
        const bgStyle = activeSwatch.getAttribute('style');
        if (bgStyle) applySwatchData(handle, bgStyle);
      }
    };

    // --- NETWORK & CACHE HANDLER ---
    uniqueHandles.forEach(handle => {
      if (!handle) return;

      // Step 1: Check Session Storage
      const cached = window.sessionStorage.getItem(`${CACHE_KEY}${handle}`);
      if (cached) {
        if (cached === '404') hideBrokenSwatches(handle);
        else parseAndApply(handle, cached);
        return;
      }

      // Step 2: Fetch Data via AJAX
      // Requires a matching Liquid template: 'product.swatch-data.liquid'
      fetch(`/products/${handle}?view=swatch-data`)
        .then(res => {
          if (!res.ok) {
            // Store '404' in cache to prevent future retry loops
            window.sessionStorage.setItem(`${CACHE_KEY}${handle}`, '404');
            hideBrokenSwatches(handle);
            return null;
          }
          return res.text();
        })
        .then(html => {
          if (!html) return;
          // Save valid HTML to cache
          try {
            window.sessionStorage.setItem(`${CACHE_KEY}${handle}`, html);
          } catch (e) {}
          parseAndApply(handle, html);
        })
        .catch(() => {
          /* Silent fail on network error */
        });
    });
  };

  // --- EVENT LISTENERS ---

  // 1. Hook into Boost Filter's native event (Primary Trigger)
  // Fires after products are rendered/filtered/sorted
  if (window.BoostPFS) {
    document.addEventListener('boost-pfs-filter-after-render', runFetcher);
  }

  // 2. Mutation Observer (Fallback Trigger)
  // Monitors DOM changes for infinite scroll or search results injection
  const observer = new MutationObserver(mutations => {
    let shouldRun = false;
    for (const m of mutations) {
      if (m.addedNodes.length > 0) {
        shouldRun = true;
        break;
      }
    }
    if (shouldRun) runFetcher();
  });

  // Observe specific Boost containers if they exist, otherwise observe Body
  const targets = document.querySelectorAll('.boost-sd__product-list');

  if (targets.length > 0) {
    targets.forEach(t => observer.observe(t, { childList: true, subtree: true }));
  } else {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Initial execution on page load
  setTimeout(runFetcher, 100);
})();
